import React, { PureComponent } from 'react'
import { hasTag } from '@ycos/coremedia-renderer'
import { createFontStyle } from '@ycos/primitives'
import { createComponent, withTheme, ThemeProvider } from '@ycos/fela'
import { shape, string, arrayOf, object, oneOf, bool, number } from 'prop-types'

import NotificationIcon from './NotificationIcon'

class ContextualNotification extends PureComponent {
  static getChevronStyles(orientation, type, colors, chevronWidth) {
    if (orientation === 'none') {
      return {}
    }

    let borderStyle = {}
    let backgroundStyle = {}
    const transparent = 'rgba(255,255,255,0.001)'

    switch (orientation) {
      case 'bottom':
        borderStyle = {
          bottom: '100%',
          left: '50%',
          marginLeft: `-${chevronWidth}px`,
          borderColor: `${transparent} ${transparent} ${colors[`${type}BorderColor`]} ${transparent}`
        }

        backgroundStyle = {
          bottom: 'calc(100% - 1px)',
          left: '50%',
          marginLeft: `-${chevronWidth}px`,
          borderColor: `${transparent} ${transparent} ${colors[`${type}BgColor`]} ${transparent}`
        }
        break
      case 'right':
        borderStyle = {
          right: '100%',
          top: '50%',
          marginTop: `-${chevronWidth}px`,
          borderColor: `${transparent} ${colors[`${type}BorderColor`]} ${transparent} ${transparent}`
        }

        backgroundStyle = {
          right: 'calc(100% - 1px)',
          top: '50%',
          marginTop: `-${chevronWidth}px`,
          borderColor: `${transparent} ${colors[`${type}BgColor`]} ${transparent} ${transparent}`
        }
        break
      case 'left':
        borderStyle = {
          left: '100%',
          top: '50%',
          marginTop: `-${chevronWidth}px`,
          borderColor: `${transparent} ${transparent} ${transparent} ${colors[`${type}BorderColor`]}`
        }

        backgroundStyle = {
          left: 'calc(100% - 1px)',
          top: '50%',
          marginTop: `-${chevronWidth}px`,
          borderColor: `${transparent} ${transparent} ${transparent} ${colors[`${type}BgColor`]}`
        }
        break
      case 'top':
        borderStyle = {
          top: '100%',
          left: '50%',
          marginLeft: `-${chevronWidth}px`,
          borderColor: `${colors[`${type}BorderColor`]} ${transparent} ${transparent} ${transparent}`
        }

        backgroundStyle = {
          top: 'calc(100% - 1px)',
          left: '50%',
          marginLeft: `-${chevronWidth}px`,
          borderColor: `${colors[`${type}BgColor`]} ${transparent} ${transparent} ${transparent}`
        }
        break
    }

    return {
      '::after': {
        content: '""',
        position: 'absolute',
        borderWidth: `${chevronWidth}px`,
        borderStyle: 'solid',
        ...borderStyle
      },

      '::before': {
        content: '""',
        position: 'absolute',
        borderWidth: `${chevronWidth}px`,
        borderStyle: 'solid',
        zIndex: 1,
        ...backgroundStyle
      }
    }
  }

  static getNotificationStylesForOrientation(orientation, container, parent, showNotification, chevronWidth, notificationMargin) {
    if ((typeof parent === 'object' && Object.keys(parent).length === 0) || (container === 'object' && Object.keys(container).length === 0)) {
      return {}
    }

    let positionProperty
    let base = {}
    const margin = showNotification ? notificationMargin : 0
    const parentProperties = parent.getBoundingClientRect()
    const { width, height } = container.getBoundingClientRect()

    switch (orientation) {
      case 'bottom':
        base = {
          top: `${parentProperties.height + chevronWidth + margin}px`,
          left: '50%',
          marginLeft: `-${width / 2}px`
        }
        positionProperty = 'top'
        break
      case 'left':
        base = {
          top: `${parentProperties.height / 2 - height / 2}px`,
          left: `${-width - margin - chevronWidth}px`
        }
        positionProperty = 'left'
        break
      case 'top':
        base = {
          bottom: `${parentProperties.height + height + chevronWidth + margin}px`,
          left: '50%',
          marginLeft: `-${width / 2}px`
        }
        positionProperty = 'bottom'
        break
      case 'right':
        base = {
          top: `${parentProperties.height / 2 - height / 2}px`,
          left: `${parentProperties.width + chevronWidth + margin}px`
        }
        positionProperty = 'left'
        break
    }

    return {
      ...base,
      transition: `opacity ${showNotification ? '300ms' : '200ms'} ${
        showNotification ? 'cubic-bezier(0.5, 0.0, 0.5, 1.0)' : 'cubic-bezier(0.0, 0.0, 0.0, 1.0)'
      }, ${positionProperty} 300ms cubic-bezier(0.0, 0.0, 1.0, 1.0)`
    }
  }

  constructor(props) {
    super(props)

    this.state = {
      orientation: props.orientation,
      parentElement: {},
      containerElement: {}
    }

    this.notificationRef = React.createRef()
  }

  componentDidMount() {
    this.setState({
      parentElement: this.notificationRef.current.parentElement,
      containerElement: this.notificationRef.current.firstChild
    })
  }

  static layoutVariant = 'isc-contextual-notification'

  getTypeFromTags() {
    const tags = this.props.item.tags

    if (hasTag(tags, 'Notification.Informational')) {
      return 'info'
    }

    if (hasTag(tags, 'Notification.Success')) {
      return 'success'
    }

    return 'error'
  }

  render() {
    const { text, orientation, type, item, extraStyles, showNotification, chevronWidth, notificationMargin, theme } = this.props
    const { teaserTextPlain } = item
    const notificationType = type || this.getTypeFromTags()
    const content = text || teaserTextPlain

    const styles =
      orientation === 'none'
        ? {}
        : {
          position: 'absolute',
          ...ContextualNotification.getNotificationStylesForOrientation(
            this.state.orientation,
            this.state.containerElement,
            this.state.parentElement,
            showNotification,
            chevronWidth,
            notificationMargin
          )
        }

    return (
      <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
        <div ref={this.notificationRef} style={styles}>
          <Container
            type={notificationType}
            extraStyles={extraStyles}
            orientation={this.state.orientation}
            showNotification={showNotification}
            data-automation="notification"
          >
            <IconContainer>
              <NotificationIcon type={notificationType} />
            </IconContainer>
            <NotificationText type={notificationType} orientation={this.state.orientation} chevronWidth={chevronWidth}>
              {content}
            </NotificationText>
          </Container>
        </div>
      </ThemeProvider>
    )
  }
}

ContextualNotification.propTypes = {
  item: shape({
    layoutVariant: string.isRequired,
    teaserTextPlain: string.isRequired,
    tags: arrayOf(object).isRequired
  }),
  text: string.isRequired,
  type: oneOf(['info', 'success', 'error']),
  extraStyles: object,
  orientation: oneOf(['none', 'top', 'right', 'bottom', 'left']),
  showNotification: bool,
  chevronWidth: number,
  notificationMargin: number
}

ContextualNotification.defaultProps = {
  item: {},
  extraStyles: {},
  orientation: 'none',
  showNotification: true,
  chevronWidth: 8,
  notificationMargin: 8
}

const Container = createComponent('Container', ({ theme, type, extraStyles, orientation, showNotification }) => {
  const { spacingMultiplier, custom } = theme

  return {
    border: `1px solid ${custom.notification[`${type}BorderColor`] || custom.notification[`${type}BgColor`]}`,
    borderRadius: '2px',
    backgroundColor: custom.notification[`${type}BgColor`],
    padding: `${spacingMultiplier}px ${spacingMultiplier}px`,
    margin: orientation === 'none' ? `${3 * spacingMultiplier}px 0` : 0,
    position: orientation === 'none' ? 'relative' : 'absolute',
    zIndex: orientation === 'none' ? undefined : 99999, // above everything else
    maxWidth: '520px',
    opacity: showNotification ? 1 : 0,

    ...extraStyles
  }
})

const IconContainer = createComponent('IconContainer', ({ theme }) => ({
  position: 'absolute',
  left: `${theme.spacingMultiplier}px`,
  top: `${theme.spacingMultiplier}px`
}))

const NotificationText = createFontStyle('NotificationText', { type: 'Label', tagName: 'p', name: '1' }, ({ theme, type, orientation, chevronWidth }) => ({
  color: theme.custom.notification[`${type}Color`],
  margin: 0,
  paddingLeft: `${4 * theme.spacingMultiplier}px`,
  minHeight: '20px',
  textAlign: 'left',

  ...ContextualNotification.getChevronStyles(orientation, type, theme.custom.notification, chevronWidth)
}))

export default withTheme(ContextualNotification)
